import type { ExchangeItemReward } from '@/types/shared/exchange'
import { allSpecialCurrenciesConfig } from '@/config/special-currencies'
import { formatNumberFullTruncate } from '@/helpers/utils'
import { formatProfitTime } from '@/helpers/profit-time'
import { rewardImageMap } from '@/config/reward-images'

export { rewardImageMap }

export interface RewardDisplayResult {
  image: string
  amount: string
  title: string
}

export function getExchangeRewardDisplay(
  reward: Partial<ExchangeItemReward>,
  goldenCoinValue: number
): RewardDisplayResult | null {
  const rewardKey = Object.keys(reward)[0] as keyof ExchangeItemReward | undefined
  if (!rewardKey) return null

  if (rewardKey === 'specialCurrencies' && reward.specialCurrencies) {
    const currencyKey = Object.keys(reward.specialCurrencies)[0] as string | undefined
    if (currencyKey) {
      return {
        image: rewardImageMap.value[currencyKey],
        amount: formatNumberFullTruncate(reward.specialCurrencies[currencyKey] ?? 0),
        title: allSpecialCurrenciesConfig.value[currencyKey]?.label ?? currencyKey,
      }
    }
  }

  if (rewardKey === 'goldenCoins' && reward.goldenCoins) {
    return {
      image: rewardImageMap.value.goldenCoins,
      amount: formatNumberFullTruncate(reward.goldenCoins * goldenCoinValue),
      title: 'Golden Coins',
    }
  }

  if (rewardKey === 'offlineProfit' && reward.offlineProfit) {
    return {
      image: rewardImageMap.value.offlineProfit,
      amount: '+' + formatProfitTime(reward.offlineProfit),
      title: 'Offline Profit',
    }
  }

  if (rewardKey === 'profitX2' && reward.profitX2) {
    return {
      image: rewardImageMap.value.profitX2,
      amount: formatProfitTime(reward.profitX2),
      title: 'Double Profit',
    }
  }

  const titleMap: Record<string, string> = {
    spins: 'Spins',
    giveawayTickets: 'Tickets',
    dropeeStars: 'Stars',
  }

  const value = reward[rewardKey as Exclude<keyof ExchangeItemReward, 'specialCurrencies'>]
  if (value) {
    return {
      image: rewardImageMap.value[rewardKey],
      amount: formatNumberFullTruncate(value),
      title: titleMap[rewardKey] ?? rewardKey,
    }
  }

  return null
}
